import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  IconButton, Button, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon, Download as DownloadIcon, Description as FileIcon,
  ZoomIn as ZoomInIcon, ZoomOut as ZoomOutIcon, RestartAlt as ResetIcon,
} from '@mui/icons-material';
import axiosInstance from '../../services/axiosInstance';

interface Props {
  open: boolean;
  onClose: () => void;
  attachmentId: number;
  fileName: string;
  fileExtension: string;
  isRTL: boolean;
}

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'];
const TEXT_EXTS = ['txt', 'csv'];
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx'];

// معاينة المرفقات داخل النظام بدون تنزيلها للجهاز.
// PDF والصور والنصوص تُعرض مباشرة؛ ملفات Office لا يمكن للمتصفح عرضها فتُتاح بالتنزيل.
const AttachmentPreviewDialog: React.FC<Props> = ({ open, onClose, attachmentId, fileName, fileExtension, isRTL }) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);

  const ext = (fileExtension || '').replace('.', '').toLowerCase();
  const isImage = IMAGE_EXTS.includes(ext);
  const isPdf = ext === 'pdf';
  const isText = TEXT_EXTS.includes(ext);
  const isOffice = OFFICE_EXTS.includes(ext);
  const canPreview = isImage || isPdf || isText;

  useEffect(() => {
    let revokeUrl: string | null = null;
    const load = async () => {
      if (!open || !canPreview) return;
      setLoading(true);
      setError(null);
      setTextContent(null);
      setBlobUrl(null);
      setZoom(1);
      try {
        const res = await axiosInstance.get(`/DocumentAttachments/download/${attachmentId}`, { responseType: 'blob' });
        const blob = res.data as Blob;
        if (isText) {
          setTextContent(await blob.text());
        } else {
          const url = URL.createObjectURL(blob);
          revokeUrl = url;
          setBlobUrl(url);
        }
      } catch {
        setError(isRTL ? 'تعذّر تحميل الملف للمعاينة' : 'Failed to load file for preview');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => { if (revokeUrl) URL.revokeObjectURL(revokeUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attachmentId]);

  const zoomIn = () => setZoom(z => Math.min(z + 0.25, 5));
  const zoomOut = () => setZoom(z => Math.max(z - 0.25, 0.25));
  const resetZoom = () => setZoom(1);
  const onWheelZoom = (e: React.WheelEvent) => {
    if (!isImage) return;
    e.preventDefault();
    setZoom(z => Math.min(5, Math.max(0.25, z + (e.deltaY < 0 ? 0.15 : -0.15))));
  };

  const handleDownload = async () => {
    try {
      const res = await axiosInstance.get(`/DocumentAttachments/download/${attachmentId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* handled by interceptor toast */ }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {fileName}
        </Typography>
        {isImage && !loading && !error && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mx: 1 }}>
            <Tooltip title={isRTL ? 'تصغير' : 'Zoom out'}>
              <span><IconButton size="small" onClick={zoomOut} disabled={zoom <= 0.25}><ZoomOutIcon /></IconButton></span>
            </Tooltip>
            <Typography variant="body2" sx={{ minWidth: 44, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
            <Tooltip title={isRTL ? 'تكبير' : 'Zoom in'}>
              <span><IconButton size="small" onClick={zoomIn} disabled={zoom >= 5}><ZoomInIcon /></IconButton></span>
            </Tooltip>
            <Tooltip title={isRTL ? 'إعادة الضبط' : 'Reset'}>
              <IconButton size="small" onClick={resetZoom}><ResetIcon /></IconButton>
            </Tooltip>
          </Box>
        )}
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ minHeight: '70vh', p: isText ? 2 : 0, bgcolor: '#525659' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <CircularProgress sx={{ color: 'white' }} />
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
            <Typography color="error" sx={{ color: '#ff8a80' }}>{error}</Typography>
          </Box>
        ) : isOffice || !canPreview ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh', gap: 2, color: 'white' }}>
            <FileIcon sx={{ fontSize: 64, opacity: 0.7 }} />
            <Typography>
              {isRTL ? 'هذا النوع من الملفات لا يمكن معاينته داخل المتصفح' : 'This file type cannot be previewed in the browser'}
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              {isRTL ? 'تنزيل الملف' : 'Download file'}
            </Button>
          </Box>
        ) : isPdf ? (
          <iframe
            title={fileName}
            src={blobUrl || ''}
            style={{ width: '100%', height: '75vh', border: 'none' }}
          />
        ) : isImage ? (
          <Box
            onWheel={onWheelZoom}
            sx={{
              height: '75vh', overflow: 'auto', p: 2,
              display: 'flex', justifyContent: 'center', alignItems: zoom > 1 ? 'flex-start' : 'center',
              cursor: zoom > 1 ? 'grab' : 'default',
            }}
          >
            <img
              src={blobUrl || ''}
              alt={fileName}
              onDoubleClick={() => setZoom(z => (z === 1 ? 2 : 1))}
              style={{
                transform: `scale(${zoom})`,
                transformOrigin: 'center top',
                maxWidth: '100%',
                maxHeight: zoom <= 1 ? '100%' : 'none',
                objectFit: 'contain',
                transition: 'transform 0.15s ease-out',
                userSelect: 'none',
              }}
            />
          </Box>
        ) : isText ? (
          <Box component="pre" sx={{
            m: 0, p: 2, bgcolor: 'white', color: 'black', borderRadius: 1,
            maxHeight: '72vh', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontFamily: 'monospace', fontSize: '0.85rem', direction: 'ltr',
          }}>
            {textContent}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'background.paper' }}>
        <Button startIcon={<DownloadIcon />} onClick={handleDownload}>
          {isRTL ? 'تنزيل' : 'Download'}
        </Button>
        <Button onClick={onClose}>{isRTL ? 'إغلاق' : 'Close'}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttachmentPreviewDialog;
